import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaCalendarDay, FaExclamationTriangle, FaHandHoldingUsd, FaSearch, FaUsers, FaWallet } from "react-icons/fa";
import { Stat } from "../../pages/dm/components/common";
import { Empty, PageHead } from "../../pages/work/WorkUi";
import billingApi, { billingError } from "./billingApi";
import { money } from "./BillingUi";
import { AGING_BUCKETS, FINANCE_SECTIONS, agingBucketKey, emptyAging, isDueWithin } from "../../utils/financeTerms";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

const DUE_SOON_DAYS = 7;

/** Finance > Receivables: what each of this company's clients still owes, and how late it is. */
function TenantReceivables() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([billingApi.summary(), billingApi.invoices({ status: "open" })])
      .then(([s, list]) => {
        if (!alive) return;
        setSummary(s);
        setInvoices(list?.invoices || []);
      })
      .catch((err) => alive && setError(billingError(err, "Could not load receivables")));
    return () => {
      alive = false;
    };
  }, []);

  const currency = summary?.currency;

  const clients = useMemo(() => {
    const byClient = new Map();
    (invoices || []).forEach((inv) => {
      const bal = Number(inv.Balance_Amount || 0);
      if (bal <= 0.001) return;
      const key = String(inv.Tenant_Client_Id);
      const row = byClient.get(key) || {
        id: inv.Tenant_Client_Id,
        name: inv.Client_Name || "Client",
        count: 0,
        balance: 0,
        overdue: 0,
        dueSoon: 0,
        oldestLate: 0,
        aging: emptyAging(),
      };
      const bucket = agingBucketKey(inv.Due_Date);
      row.count += 1;
      row.balance += bal;
      row.aging[bucket] += bal;
      if (bucket !== "current") row.overdue += bal;
      if (isDueWithin(inv.Due_Date, DUE_SOON_DAYS)) row.dueSoon += bal;
      row.oldestLate = Math.max(row.oldestLate, Number(inv.Days_Overdue || 0));
      byClient.set(key, row);
    });
    return Array.from(byClient.values()).sort((a, b) => b.overdue - a.overdue || b.balance - a.balance);
  }, [invoices]);

  const dueSoon = clients.reduce((s, c) => s + c.dueSoon, 0);
  const q = search.trim().toLowerCase();
  const rows = clients.filter((c) => (!onlyOverdue || c.overdue > 0.001) && (!q || c.name.toLowerCase().includes(q)));
  const openClient = (id) => navigate(`/portal/finance/invoices?status=open&client=${id}`);

  return (
    <div className="dmp wk bl">
      <PageHead
        title={FINANCE_SECTIONS.receivables.label}
        info={`${FINANCE_SECTIONS.receivables.hint}. Each client's unpaid invoices are added up and split by how many days past the due date they are. Open a client to see the invoices and record payments.`}
      />

      <div className="dmp-stats">
        <button type="button" className="wk-tile-btn" onClick={() => setOnlyOverdue(false)}>
          <Stat label="Clients owe you" value={summary ? money(summary.outstanding, currency) : "—"} icon={FaWallet} tone="blue" />
        </button>
        <button type="button" className="wk-tile-btn" onClick={() => setOnlyOverdue(true)}>
          <Stat
            label={`Overdue${summary?.overdue_count ? ` (${summary.overdue_count})` : ""}`}
            value={summary ? money(summary.overdue, currency) : "—"}
            icon={FaExclamationTriangle}
            tone="rose"
          />
        </button>
        <div className="wk-tile-btn">
          <Stat label="Due this week" value={invoices ? money(dueSoon, currency) : "—"} icon={FaCalendarDay} tone="amber" />
        </div>
        <Link className="wk-tile-btn" to="/portal/finance/payments-received">
          <Stat
            label="Received this month"
            value={summary ? money(summary.received_this_month, currency) : "—"}
            icon={FaHandHoldingUsd}
            tone="green"
          />
        </Link>
      </div>

      <div className="dmp-card">
        <div className="wk-proj-toolbar">
          <div className="wk-search">
            <FaSearch aria-hidden="true" className="wk-search__icon" />
            <label className="wk-sr-only" htmlFor="bl-recv-search">
              Search clients
            </label>
            <input
              id="bl-recv-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Client name"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="wk-chip-row" role="group" aria-label="Show">
          <button type="button" className="wk-chip" aria-pressed={!onlyOverdue} onClick={() => setOnlyOverdue(false)}>
            All who owe you
          </button>
          <button type="button" className="wk-chip" aria-pressed={onlyOverdue} onClick={() => setOnlyOverdue(true)}>
            Overdue only
          </button>
        </div>
        {error ? <div className="dmp-error">{error}</div> : null}
        {!invoices ? (
          <div className="wk-muted">Loading…</div>
        ) : rows.length ? (
          <table className="wk-table wk-proj-table bl-table">
            <thead>
              <tr>
                <th>Client</th>
                <th className="bl-num">Unpaid invoices</th>
                <th className="bl-num">Owes you</th>
                {AGING_BUCKETS.map((b) => (
                  <th key={b.key} className="bl-num">
                    {b.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="wk-proj-row" onClick={() => openClient(c.id)}>
                  <td data-label="Client">
                    <Link className="wk-proj-name" to={`/portal/finance/invoices?status=open&client=${c.id}`} onClick={(e) => e.stopPropagation()}>
                      {c.name}
                    </Link>
                    {c.oldestLate > 0 ? <div className="bl-late">Oldest {c.oldestLate} days late</div> : null}
                  </td>
                  <td data-label="Unpaid invoices" className="bl-num">
                    {c.count}
                  </td>
                  <td data-label="Owes you" className="bl-num">
                    <strong>{money(c.balance, currency)}</strong>
                  </td>
                  {AGING_BUCKETS.map((b) => (
                    <td key={b.key} data-label={b.label} className={`bl-num${b.key !== "current" && c.aging[b.key] > 0.001 ? " bl-late" : ""}`}>
                      {c.aging[b.key] > 0.001 ? money(c.aging[b.key], currency) : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty icon={FaUsers} title={onlyOverdue ? "Nobody is late" : "Nobody owes you anything"}>
            {onlyOverdue ? "Every unpaid invoice is still within its due date." : "Unpaid invoices show up here, grouped by client."}
          </Empty>
        )}
      </div>
    </div>
  );
}

export default TenantReceivables;
